import { useState } from 'react';
import { useWalletClient } from 'wagmi';
import { toast } from 'sonner';
import { executeOrder, type OrderExecutionParams, type OrderResult } from '../services/orderExecution';
import type { Hip3Market } from '../types';

export function useOrderExecution() {
  const { data: walletClient } = useWalletClient();
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastResult, setLastResult] = useState<OrderResult | null>(null);

  // Get builder config from environment
  const builderAddress = import.meta.env.VITE_BUILDER_ADDRESS;
  const builderFeeBps = import.meta.env.VITE_BUILDER_FEE_BPS
    ? parseFloat(import.meta.env.VITE_BUILDER_FEE_BPS)
    : undefined;
  const useTestnet = import.meta.env.VITE_ENABLE_TESTNET === 'true';

  const execute = async (
    market: Hip3Market,
    side: 'buy' | 'sell',
    size: number,
    type: 'market' | 'limit',
    price?: number,
    reduceOnly?: boolean
  ): Promise<OrderResult> => {
    if (!walletClient) {
      const error: OrderResult = {
        success: false,
        error: 'Wallet not connected',
      };
      setLastResult(error);
      toast.error('Please connect your wallet first');
      return error;
    }

    setIsExecuting(true);
    setLastResult(null);

    try {
      const params: OrderExecutionParams = {
        market,
        side,
        size,
        type,
        price,
        reduceOnly,
        walletClient: walletClient as any, // Type assertion for compatibility
        builderAddress,
        builderFeeBps,
        useTestnet,
      };

      const result = await executeOrder(params);
      setLastResult(result);

      // Show toast notification
      if (result.success) {
        if (result.filledSize && result.avgPrice) {
          toast.success(`Order filled: ${side.toUpperCase()} ${result.filledSize} ${market.baseAsset} @ $${result.avgPrice}`);
        } else {
          toast.info(`Order placed: ${side.toUpperCase()} ${size} ${market.baseAsset}`);
        }
      } else {
        toast.error(result.error || 'Order failed');
      }

      return result;
    } catch (error) {
      // executeOrder handles errors internally, but catch any unexpected errors
      const errorMessage = error instanceof Error ? error.message : 'Execution failed';
      const errorResult: OrderResult = {
        success: false,
        error: errorMessage,
      };
      setLastResult(errorResult);
      // Only show toast if it's not already shown by the result handling above
      // This prevents duplicate toasts
      return errorResult;
    } finally {
      setIsExecuting(false);
    }
  };

  return {
    execute,
    isExecuting,
    lastResult,
  };
}
